import { type LocaleCode, SUPPORTED_LOCALES } from '../../config/locales';

/** `localStorage` key under which the chosen language is remembered, next to `fury-theme`. */
export const LOCALE_STORAGE_KEY = 'fury-locale';

/**
 * Finds the supported locale a language tag belongs to, matching on the primary language only.
 *
 * @param tag BCP 47 tag such as `pt-BR`, or `null`/`undefined` when there is none.
 * @returns The supported locale code, or `null` when the language is not supported.
 * @example
 * matchSupportedLocale('pt-BR'); // 'pt'
 * matchSupportedLocale('fr'); // null
 */
export function matchSupportedLocale(tag: string | null | undefined): LocaleCode | null {
  if (!tag) return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LOCALES.find((locale) => locale.code === primary)?.code ?? null;
}

/**
 * Picks the language to start with: the remembered one if it is supported, otherwise the first
 * browser language that is supported, otherwise Spanish.
 *
 * @param stored Value read from storage; anything that is not a supported code is ignored.
 * @param browserLanguages Browser languages in order of preference (`navigator.languages`).
 * @example
 * resolveInitialLocale(null, ['pt-BR', 'en']); // 'pt'
 * resolveInitialLocale('xx', ['fr']); // 'es'
 */
export function resolveInitialLocale(
  stored: string | null,
  browserLanguages: readonly string[],
): LocaleCode {
  const remembered = SUPPORTED_LOCALES.find((locale) => locale.code === stored);
  if (remembered) return remembered.code;
  for (const tag of browserLanguages) {
    const match = matchSupportedLocale(tag);
    if (match) return match;
  }
  return SUPPORTED_LOCALES.find((locale) => locale.isDefault)?.code ?? 'es';
}
